import { useState, useEffect, useRef } from "react";
import './certificatesShowcase.css';

interface CertyficateGalleryProps {
  certs: string[];
}

const AUTO_SLIDE_DURATION = 5000;
const PROGRESS_BAR_STEP_TIME = 50;

const getVisibleCount = () => {
  if (window.innerWidth < 600) return 2;
  if (window.innerWidth < 900) return 3;
  if (window.innerWidth < 1200) return 4;
  return 5;
};

export const CertificatesShowcase = ({ certs }: CertyficateGalleryProps) => {
  const [startIdx, setStartIdx] = useState(0);
  const [visibleCount, setVisibleCount] = useState(getVisibleCount());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCertSrc, setCurrentCertSrc] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleCount());
      setElapsedTime(0);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!certs || certs.length === 0 || isModalOpen || isHovering) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      setStartIdx((prev) => (prev + visibleCount) % certs.length);
      setElapsedTime(0);
    }, AUTO_SLIDE_DURATION);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [certs, visibleCount, isModalOpen, isHovering]);

  useEffect(() => {
     if (!certs || certs.length === 0 || isModalOpen || isHovering) {
         if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
         return;
     }

     if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);

     progressIntervalRef.current = setInterval(() => {
         setElapsedTime(prevTime => {
             const newTime = prevTime + PROGRESS_BAR_STEP_TIME;
             return Math.min(newTime, AUTO_SLIDE_DURATION);
         });
     }, PROGRESS_BAR_STEP_TIME);

     return () => {
         if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
     };
  }, [startIdx, isModalOpen, isHovering, certs.length]);

  const handleMouseEnter = () => {
      setIsHovering(true);
  };

  const handleMouseLeave = () => {
      setIsHovering(false);
  };

  const handlePrev = () => {
    if (isModalOpen) return;
    setStartIdx((prev) => (prev - visibleCount + certs.length) % certs.length);
    setElapsedTime(0);
  };

  const handleNext = () => {
    if (isModalOpen) return;
    setStartIdx((prev) => (prev + visibleCount) % certs.length);
    setElapsedTime(0);
  };

  const openModal = (src: string) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);

    setCurrentCertSrc(src);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentCertSrc('');
    document.body.style.overflow = 'auto';
  };

  if (!certs || certs.length === 0) {
    return null;
  }

  const numCertsToShow = Math.min(certs.length, visibleCount);
  const visibleCertificates = [];
  for (let i = 0; i < numCertsToShow; i++) {
    visibleCertificates.push(certs[(startIdx + i) % certs.length]);
  }

  const progressBarWidth = (elapsedTime / AUTO_SLIDE_DURATION) * 100;

  const containerMinHeight = 200 + (8 * 2);

  return (
    <>
      <div style={{
        position: 'relative',
        width: '100%',
          minWidth: '300px',
        maxWidth: 1200,
        margin: '0 auto',
        minHeight: containerMinHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}>
        <div
          onClick={handlePrev}
          className={`cert-arrow cert-arrow-left ${isModalOpen ? 'disabled' : ''}`}
          style={{ zIndex: 1, cursor: isModalOpen ? 'not-allowed' : 'pointer' }}
        >
          <svg width="32" height="32" viewBox="0 0 32 32"><polygon points="24,4 8,16 24,28" /></svg>
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            gap: 24,
            padding: '0 40px',
            overflowX: 'hidden',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {visibleCertificates.map((src, idx) => (
            <div
              key={idx}
              style={{
                minWidth: `calc((100% - ${((visibleCount - 1) * 24)}px) / ${visibleCount})`,
                borderRadius: 8,
                padding: 8,
                background: "#fff",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                cursor: 'pointer',
              }}
              onClick={() => openModal(src)}
            >
              <img
                src={src}
                alt={`Certyfikat ${startIdx + idx + 1}`}
                style={{ height: 200, borderRadius: 4, maxWidth: '100%', objectFit: 'contain' }}
              />
            </div>
          ))}
        </div>

        <div
          onClick={handleNext}
          className={`cert-arrow cert-arrow-right ${isModalOpen ? 'disabled' : ''}`}
          style={{ zIndex: 1, cursor: isModalOpen ? 'not-allowed' : 'pointer' }}
        >
          <svg width="32" height="32" viewBox="0 0 32 32"><polygon points="8,4 24,16 8,28" /></svg>
        </div>
      </div>

      {!isModalOpen && certs.length > 0 && (
        <div style={{
          width: '50%',
          maxWidth: 1200,
          margin: '8px auto',
          height: '4px',
          backgroundColor: '#e0e0e0',
          borderRadius: '2px',
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${progressBarWidth}%`,
            height: '100%',
            backgroundColor: '#888',
            transition: 'width 100ms linear',
          }}></div>
        </div>
      )}

      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            cursor: 'pointer',
          }}
          onClick={closeModal}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: '90%',
              maxHeight: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentCertSrc}
              alt="Enlarged Certificate"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: 8,
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};
